import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { decisionsApi, type ScoresData, type GraphData, type GlobalScoresData, type DecisionsData } from '../../services/api/decisions.api';
import { sessionsApi } from '../../services/api/sessions.api';
import { settingsApi } from '../../services/api/settings.api';
import {
  adminRealtimeEvents,
  type AdminRoundSubmittedEvent,
  type AdminTeamResetEvent,
  type GameSession,
  type TeamName,
} from '@business-game/shared';
import { useTranslation } from 'react-i18next';
import { getAdminRealtimeSocket } from '../../services/realtime/admin.socket';
import { applyPlayerLanguage } from '../../utils/sessionI18n';

const AnimatedProgressBar = ({ value, color }: { value: number, color: string }) => {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    // Small delay to ensure the component is mounted and rendered at 0 width first
    // before transitioning to the target value.
    const timer = setTimeout(() => {
      setWidth(Math.min(100, Math.max(0, value)));
    }, 100);
    return () => clearTimeout(timer);
  }, [value]);

  return (
    <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
      <div
        className={`${color} h-full rounded-full opacity-80 transition-all duration-1000 ease-out`}
        style={{ width: `${width}%` }}
      ></div>
    </div>
  );
};

const SortableHeader = ({
  label,
  active,
  direction,
  onClick,
}: {
  label: string;
  active: boolean;
  direction: 'asc' | 'desc';
  onClick: () => void;
}) => (
  <th className="p-4 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 transition-colors ${active ? 'text-slate-900' : 'hover:text-slate-700'}`}
    >
      <span>{label}</span>
      <span className={`text-[10px] ${active ? 'text-indigo-600' : 'text-slate-400'}`}>
        {active ? (direction === 'asc' ? '↑' : '↓') : '↕'}
      </span>
    </button>
  </th>
);

// --- Team Theme Configuration ---
const teamConfigs = [
  {
    name: 'team1' as TeamName,
    color: 'indigo',
    icon: (
      <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    watermark: (
      <svg fill="currentColor" viewBox="0 0 24 24">
        <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
  },
  {
    name: 'team2' as TeamName,
    color: 'emerald',
    icon: (
      <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    watermark: (
      <svg fill="currentColor" viewBox="0 0 24 24">
        <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
  {
    name: 'team3' as TeamName,
    color: 'blue',
    icon: (
      <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
      </svg>
    ),
    watermark: (
      <svg fill="currentColor" viewBox="0 0 24 24">
        <path d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
      </svg>
    ),
  },
  {
    name: 'team4' as TeamName,
    color: 'orange',
    icon: (
      <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    watermark: (
      <svg fill="currentColor" viewBox="0 0 24 24">
        <path d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
];

const teamColorUtils = {
  indigo: {
    bg: 'bg-indigo-50',
    text: 'text-indigo-600',
    border: 'border-indigo-200',
    gradient: 'from-indigo-600 to-indigo-400',
    button: 'bg-indigo-600 hover:bg-indigo-700',
    shadow: 'shadow-indigo-200',
    ring: 'focus:ring-indigo-500',
    lightBg: 'bg-indigo-50',
    headerBg: 'bg-indigo-100/50',
  },
  emerald: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-600',
    border: 'border-emerald-200',
    gradient: 'from-emerald-600 to-emerald-400',
    button: 'bg-emerald-600 hover:bg-emerald-700',
    shadow: 'shadow-emerald-200',
    ring: 'focus:ring-emerald-500',
    lightBg: 'bg-emerald-50',
    headerBg: 'bg-emerald-100/50',
  },
  blue: {
    bg: 'bg-blue-50',
    text: 'text-blue-600',
    border: 'border-blue-200',
    gradient: 'from-blue-600 to-blue-400',
    button: 'bg-blue-600 hover:bg-blue-700',
    shadow: 'shadow-blue-200',
    ring: 'focus:ring-blue-500',
    lightBg: 'bg-blue-50',
    headerBg: 'bg-blue-100/50',
  },
  orange: {
    bg: 'bg-orange-50',
    text: 'text-orange-600',
    border: 'border-orange-200',
    gradient: 'from-orange-600 to-orange-400',
    button: 'bg-orange-600 hover:bg-orange-700',
    shadow: 'shadow-orange-200',
    ring: 'focus:ring-orange-500',
    lightBg: 'bg-orange-50',
    headerBg: 'bg-orange-100/50',
  },
};




export const Decisions: React.FC = () => {
  const { t } = useTranslation();
  const { teamNumber: teamNumberParam } = useParams<{ teamNumber?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [teamNumber, setTeamNumber] = useState<number>(
    teamNumberParam ? parseInt(teamNumberParam, 10) : 1
  );
  const [sessionCode, setSessionCode] = useState<string | undefined>(
    searchParams.get('session') || undefined
  );
  const [sessions, setSessions] = useState<GameSession[]>([]);
  const [maxRounds, setMaxRounds] = useState(10);
  const [teamProgress, setTeamProgress] = useState<Record<number, { roundsPlayed: number; isCompleted: boolean }>>({});

  const [scoresData, setScoresData] = useState<ScoresData | null>(null);
  const [graphData, setGraphData] = useState<GraphData | null>(null);
  const [globalScores, setGlobalScores] = useState<GlobalScoresData[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [decisionsData, setDecisionsData] = useState<DecisionsData | null>(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetTargetTeamNumber, setResetTargetTeamNumber] = useState<number | null>(null);
  const [resetting, setResetting] = useState(false);
  const [liveStatus, setLiveStatus] = useState<'connecting' | 'connected' | 'disconnected'>('connecting');
  const [linkCopied, setLinkCopied] = useState(false);
  const [isGraphFullscreen, setIsGraphFullscreen] = useState(false);
  const [rankingSort, setRankingSort] = useState<{
    column: 'total' | 'impact1' | 'impact2' | 'impact3' | 'impact4';
    direction: 'asc' | 'desc';
  }>({ column: 'total', direction: 'desc' });

  // Navigation State
  const [activeTab, setActiveTab] = useState<'overview' | 'decisions' | 'scores'>('overview');



  const chartContainerRef = useRef<HTMLDivElement>(null);
  const rankingChartContainerRef = useRef<HTMLDivElement>(null);
  const graphCardRef = useRef<HTMLDivElement>(null);
  const apexChartsRef = useRef<any>(null);
  const rankingApexChartsRef = useRef<any>(null);
  const liveRefreshTimeoutRef = useRef<number | null>(null);



  // Computed Team Theme
  const config = teamConfigs[Math.max(0, teamNumber - 1) % 4];
  const theme = teamColorUtils[config.color as keyof typeof teamColorUtils];
  const playedRoundsCount = (data?: DecisionsData | null) =>
    data?.rounds.filter((round) => round.total > 0).length ?? 0;
  const getProgressPercent = (roundsPlayed: number) =>
    maxRounds > 0 ? Math.round((Math.min(roundsPlayed, maxRounds) / maxRounds) * 100) : 0;
  const currentTeamRoundsPlayed = teamNumber > 0
    ? (teamProgress[teamNumber]?.roundsPlayed ?? playedRoundsCount(decisionsData))
    : 0;
  const currentTeamCompleted = teamNumber > 0 && currentTeamRoundsPlayed >= maxRounds;
  const currentTeamProgressPercent = getProgressPercent(currentTeamRoundsPlayed);
  const getTotalScore = useCallback(
    (scores: { impact1: number; impact2: number; impact3: number; impact4: number | null }) =>
      scores.impact1 + scores.impact2 + scores.impact3 + (scores.impact4 ?? 0),
    []
  );
  const sortedGlobalScores = useMemo(() => {
    if (!globalScores) {
      return [];
    }

    const getSortValue = (team: GlobalScoresData) => {
      if (rankingSort.column === 'total') {
        return getTotalScore(team.finalScores);
      }

      return team.finalScores[rankingSort.column] ?? 0;
    };

    return [...globalScores].sort((a, b) => {
      const result = getSortValue(a) - getSortValue(b);
      return rankingSort.direction === 'asc' ? result : -result;
    });
  }, [getTotalScore, globalScores, rankingSort]);

  const toggleRankingSort = useCallback(
    (column: 'total' | 'impact1' | 'impact2' | 'impact3' | 'impact4') => {
      setRankingSort((current) =>
        current.column === column
          ? { column, direction: current.direction === 'asc' ? 'desc' : 'asc' }
          : { column, direction: 'desc' }
      );
    },
    []
  );
  const playerLink = useMemo(() => {
    if (!sessionCode || typeof window === 'undefined') {
      return '';
    }

    return `${window.location.origin}/select-player/${sessionCode}`;
  }, [sessionCode]);

  const copyPlayerLink = useCallback(async () => {
    if (!playerLink) {
      return;
    }

    try {
      await navigator.clipboard.writeText(playerLink);
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 1800);
    } catch (error) {
      console.error('Failed to copy player link:', error);
    }
  }, [playerLink]);

  const toggleGraphFullscreen = useCallback(async () => {
    if (!graphCardRef.current) {
      return;
    }

    try {
      if (document.fullscreenElement === graphCardRef.current) {
        await document.exitFullscreen();
      } else {
        await graphCardRef.current.requestFullscreen();
      }
    } catch (error) {
      console.error('Failed to toggle graph fullscreen:', error);
    }
  }, []);

  useEffect(() => {
    loadSessions();
  }, []);

  useEffect(() => {
    if (teamNumberParam) {
      const num = parseInt(teamNumberParam, 10);
      if (!isNaN(num) && num >= 0 && num <= 4) {
        setTeamNumber(num);
      }
    }
  }, [teamNumberParam]);

  useEffect(() => {
    const sessionParam = searchParams.get('session');
    setSessionCode(sessionParam || undefined);
  }, [searchParams]);

  useEffect(() => {
    loadData();
  }, [teamNumber, sessionCode]);

  useEffect(() => {
    const syncSessionLanguage = async () => {
      if (!sessionCode) {
        return;
      }

      try {
        const session = sessions.find((entry) => entry.code === sessionCode);

        if (session) {
          await applyPlayerLanguage(session.language, session.gameMode);
          return;
        }

        const sessionCheck = await sessionsApi.checkSession(sessionCode);
        if (sessionCheck.session) {
          await applyPlayerLanguage(sessionCheck.session.language, sessionCheck.session.gameMode);
        }
      } catch (error) {
        console.error('Failed to sync decisions language with session:', error);
      }
    };

    void syncSessionLanguage();
  }, [sessionCode, sessions]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreen = document.fullscreenElement === graphCardRef.current;
      setIsGraphFullscreen(isFullscreen);

      window.setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 120);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const handleReset = async () => {
    const targetTeamNumber = resetTargetTeamNumber ?? teamNumber;

    if (targetTeamNumber <= 0) {
      return;
    }

    try {
      setResetting(true);
      await decisionsApi.resetTeam(targetTeamNumber, sessionCode);
      await loadData({ silent: true });
      setShowResetModal(false);
      setResetTargetTeamNumber(null);
    } catch (error) {
      console.error('Failed to reset team:', error);
    } finally {
      setResetting(false);
    }
  };

  const openResetModal = (targetTeamNumber: number) => {
    setResetTargetTeamNumber(targetTeamNumber);
    setShowResetModal(true);
  };

  const loadSessions = useCallback(async () => {
    try {
      const data = await sessionsApi.getAllSessions(true);
      setSessions(data);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    }
  }, []);

  const loadGameConfig = useCallback(async () => {
    try {
      const config = await settingsApi.getGameConfig();
      setMaxRounds(config.numberOfRounds || 10);
    } catch (error) {
      console.error('Failed to load game config:', error);
      setMaxRounds(10);
    }
  }, []);

  useEffect(() => {
    loadGameConfig();
  }, [loadGameConfig]);

  const loadData = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }

      if (teamNumber === 0) {
        const [global, progressEntries] = await Promise.all([
          decisionsApi.getGlobalScores(sessionCode),
          Promise.all(
            [1, 2, 3, 4].map(async (teamNum) => {
              const decisions = await decisionsApi.getDecisions(teamNum, sessionCode);
              const roundsPlayed = playedRoundsCount(decisions);

              return [
                teamNum,
                {
                  roundsPlayed,
                  isCompleted: roundsPlayed >= maxRounds,
                },
              ] as const;
            })
          ),
        ]);

        setGlobalScores(global);
        setTeamProgress(Object.fromEntries(progressEntries));
        // Reset individual data

        setScoresData(null);
        setGraphData(null);
      } else {
        const [decisions, scores, graph] = await Promise.all([
          decisionsApi.getDecisions(teamNumber, sessionCode),
          decisionsApi.getScores(teamNumber, sessionCode),
          decisionsApi.getGraph(teamNumber, sessionCode),
        ]);

        setDecisionsData(decisions);
        setScoresData(scores);
        setGraphData(graph);
        setTeamProgress((prev) => ({
          ...prev,
          [teamNumber]: {
            roundsPlayed: playedRoundsCount(decisions),
            isCompleted: playedRoundsCount(decisions) >= maxRounds,
          },
        }));
        setGlobalScores(null);
      }
    } catch (error) {
      console.error('Failed to load decisions data:', error);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [maxRounds, teamNumber, sessionCode]);

  useEffect(() => {
    const socket = getAdminRealtimeSocket();

    const scheduleRefresh = () => {
      if (!sessionCode) {
        return;
      }

      if (liveRefreshTimeoutRef.current) {
        window.clearTimeout(liveRefreshTimeoutRef.current);
      }

      liveRefreshTimeoutRef.current = window.setTimeout(() => {
        void loadData({ silent: true });
      }, 150);
    };

    const handleConnect = () => {
      setLiveStatus('connected');

      if (sessionCode) {
        socket.emit(adminRealtimeEvents.joinSession, { sessionCode });
      }
    };

    const handleDisconnect = () => {
      setLiveStatus('disconnected');
    };

    const handleRoundSubmitted = (event: AdminRoundSubmittedEvent) => {
      if (!sessionCode || event.sessionCode !== sessionCode) {
        return;
      }

      scheduleRefresh();
    };

    const handleTeamReset = (event: AdminTeamResetEvent) => {
      if (!sessionCode || event.sessionCode !== sessionCode) {
        return;
      }

      scheduleRefresh();
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on(adminRealtimeEvents.roundSubmitted, handleRoundSubmitted);
    socket.on(adminRealtimeEvents.teamReset, handleTeamReset);

    if (socket.connected) {
      handleConnect();
    } else {
      setLiveStatus('connecting');
      socket.connect();
    }

    return () => {
      if (sessionCode && socket.connected) {
        socket.emit(adminRealtimeEvents.leaveSession, { sessionCode });
      }

      if (liveRefreshTimeoutRef.current) {
        window.clearTimeout(liveRefreshTimeoutRef.current);
        liveRefreshTimeoutRef.current = null;
      }

      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off(adminRealtimeEvents.roundSubmitted, handleRoundSubmitted);
      socket.off(adminRealtimeEvents.teamReset, handleTeamReset);
      socket.disconnect();
    };
  }, [loadData, loadSessions, sessionCode]);

  const currentSession = sessions.find(s => s.code === sessionCode);
  const gameMode = currentSession?.gameMode || 'teamlab';
  const isTeamlab = gameMode === 'teamlab';
  // Impact labels depend on the game mode: TeamLab uses Performance/Cohésion/Bien-être,
  // LeadLab uses Performance/Social/Sociétal/Environnement.
  const impactLabels = useMemo(
    () =>
      isTeamlab
        ? {
            impact1: t('game.impact_performance'),
            impact2: t('game.impact_cohesion'),
            impact3: t('game.impact_wellbeing'),
            impact4: t('game.impact_environment'),
          }
        : {
            impact1: t('game.impact_performance'),
            impact2: t('game.impact_social'),
            impact3: t('game.impact_societal'),
            impact4: t('game.impact_environment'),
          },
    [isTeamlab, t]
  );
  const liveBadge =
    liveStatus === 'connected'
      ? {
          dotClass: 'bg-emerald-500',
          label: 'Live',
          textClass: 'text-emerald-600',
        }
      : liveStatus === 'connecting'
        ? {
            dotClass: 'bg-amber-500 animate-pulse',
            label: 'Connexion...',
            textClass: 'text-amber-600',
          }
        : {
            dotClass: 'bg-slate-400',
            label: 'Hors ligne',
            textClass: 'text-slate-500',
          };

  // --- Graph Effect ---
  useEffect(() => {
    if (!graphData || !chartContainerRef.current || graphData.datasets.length === 0) {
      if (apexChartsRef.current) {
        apexChartsRef.current.destroy();
        apexChartsRef.current = null;
      }
      return;
    }

    import('apexcharts').then((ApexChartsModule) => {
      const ApexCharts = ApexChartsModule.default;
      if (apexChartsRef.current) apexChartsRef.current.destroy();

      const series = [
        { name: impactLabels.impact1, data: graphData.datasets[0]?.data || [] },
        { name: impactLabels.impact2, data: graphData.datasets[1]?.data || [] },
        { name: impactLabels.impact3, data: graphData.datasets[2]?.data || [] },
      ];

      if (!isTeamlab && graphData.datasets[3]) {
        series.push({ name: impactLabels.impact4, data: graphData.datasets[3].data });
      }

      const translatedLabels = graphData.labels.map((label) => {
        if (label === 'Start' || label === 'Début' || label === 'Inicio') return t('game.graph_start');
        const periodMatch = label.match(/(?:Period|Période|Período)\s+(\d+)/i);
        return periodMatch ? t('game.graph_period', { number: periodMatch[1] }) : label;
      });

      const options = {
        series,
        chart: {
          type: 'area' as const,
          height: 480,
          fontFamily: 'Inter, sans-serif',
          toolbar: { show: false },
          background: 'transparent',
          zoom: { enabled: false },
        },
        colors: isTeamlab
          ? ['#3b82f6', '#ec4899', '#eab308']
          : ['#3b82f6', '#ec4899', '#eab308', '#10b981'],
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 3 },
        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0.1, stops: [0, 90, 100] } },
        xaxis: { categories: translatedLabels, axisBorder: { show: false }, axisTicks: { show: false } },
        yaxis: { show: true },
        grid: {
          borderColor: '#f1f5f9',
          strokeDashArray: 4,
          xaxis: { lines: { show: true } },
        },
        tooltip: { theme: 'light' },
        legend: { position: 'top', horizontalAlign: 'right', offsetY: 8 },
      };

      if (!chartContainerRef.current) {
        return;
      }

      const chart = new ApexCharts(chartContainerRef.current, options as any);
      chart.render();
      apexChartsRef.current = chart;
    });

    return () => {
      if (apexChartsRef.current) {
        apexChartsRef.current.destroy();
        apexChartsRef.current = null;
      }
    };
  }, [graphData, impactLabels, isTeamlab, t]);

  useEffect(() => {
    if (teamNumber !== 0 || !rankingChartContainerRef.current || sortedGlobalScores.length === 0) {
      if (rankingApexChartsRef.current) {
        rankingApexChartsRef.current.destroy();
        rankingApexChartsRef.current = null;
      }
      return;
    }

    import('apexcharts').then((ApexChartsModule) => {
      const ApexCharts = ApexChartsModule.default;
      if (rankingApexChartsRef.current) {
        rankingApexChartsRef.current.destroy();
      }

      const longestLabels =
        sortedGlobalScores.reduce<string[]>(
          (currentLongest, team) =>
            team.graphData.labels.length > currentLongest.length ? team.graphData.labels : currentLongest,
          []
        ) || [];

      const translatedLabels = longestLabels.map((label) => {
        if (label === 'Start' || label === 'Début' || label === 'Inicio') return t('game.graph_start');
        const periodMatch = label.match(/(?:Period|Période|Período)\s+(\d+)/i);
        return periodMatch ? t('game.graph_period', { number: periodMatch[1] }) : label;
      });

      const teamLineColors = ['#6366f1', '#10b981', '#3b82f6', '#f97316'];
      const series = sortedGlobalScores.map((team, index) => {
        const teamTotals = translatedLabels.map((_, labelIndex) => {
          const totalAtIndex = team.graphData.datasets.reduce(
            (sum, dataset) => sum + (dataset.data[labelIndex] ?? 0),
            0
          );

          if (labelIndex < team.graphData.labels.length) {
            return totalAtIndex;
          }

          const previousIndex = Math.max(0, team.graphData.labels.length - 1);
          return team.graphData.datasets.reduce(
            (sum, dataset) => sum + (dataset.data[previousIndex] ?? 0),
            0
          );
        });

        return {
          name: `Équipe ${team.teamNumber}`,
          data: teamTotals,
          color: teamLineColors[index % teamLineColors.length],
        };
      });

      const options = {
        series,
        chart: {
          type: 'line' as const,
          height: 380,
          toolbar: { show: false },
          background: 'transparent',
          fontFamily: 'Inter, sans-serif',
        },
        colors: series.map((entry) => entry.color),
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth' as const, width: 4 },
        markers: { size: 5, hover: { size: 7 } },
        xaxis: {
          categories: translatedLabels,
          labels: {
            style: { colors: '#64748b' },
          },
        },
        yaxis: {
          labels: {
            style: { colors: '#64748b' },
          },
        },
        grid: {
          borderColor: '#e2e8f0',
          strokeDashArray: 4,
          xaxis: { lines: { show: true } },
        },
        legend: { position: 'top', horizontalAlign: 'right' as const },
        tooltip: {
          theme: 'light' as const,
          y: {
            formatter: (value: number) => `${value} pts`,
          },
        },
      };

      if (!rankingChartContainerRef.current) {
        return;
      }

      const chart = new ApexCharts(rankingChartContainerRef.current, options as any);
      chart.render();
      rankingApexChartsRef.current = chart;
    });

    return () => {
      if (rankingApexChartsRef.current) {
        rankingApexChartsRef.current.destroy();
        rankingApexChartsRef.current = null;
      }
    };
  }, [isTeamlab, sortedGlobalScores, t, teamNumber]);



  // Matrix Data Logic
  const matrixData = React.useMemo(() => {
    if (!decisionsData) return null;
    const CRITERIA_COLORS = [
      { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-800' },
      { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-800' },
      { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', badge: 'bg-purple-100 text-purple-800' },
      { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-800' },
      { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', badge: 'bg-rose-100 text-rose-800' },
      { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200', badge: 'bg-cyan-100 text-cyan-800' },
      { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', badge: 'bg-indigo-100 text-indigo-800' },
    ];

    return decisionsData.criterias.map((criteria, index) => {
      const rowScores = decisionsData.rounds.map(r => {
        const found = r.decisions.find(d => d.criteriaId === criteria.id);
        return found ? found.points : 0;
      });
      const total = rowScores.reduce((a, b) => a + b, 0);
      return { ...criteria, rowScores, total, theme: CRITERIA_COLORS[index % CRITERIA_COLORS.length] };
    });
  }, [decisionsData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 relative">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-200 border-t-indigo-600 relative z-10"></div>
      </div>
    );
  }





  return (
    <div className="min-h-screen flex bg-slate-50 font-sans text-slate-900 overflow-hidden">
      {/* Background Layer */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:24px_24px]"></div>
      </div>

      {/* Sidebar Navigation - compact */}
      <div className="w-16 bg-white border-r border-slate-200 flex flex-col items-center py-4 z-10 shadow-xl fixed h-full left-0 top-0">
        <div onClick={() => navigate('/admin')} className="w-10 h-10 bg-slate-900 text-white rounded-xl flex items-center justify-center cursor-pointer hover:scale-105 transition-transform" title="Retour admin">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.384-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
          </svg>
        </div>
        <button
          onClick={() => setActiveTab('overview')}
          className={`mt-6 w-10 h-10 rounded-xl font-bold transition-all flex items-center justify-center ${activeTab === 'overview' ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}`}
          title="Vue d'ensemble"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
          </svg>
        </button>
      </div>

      {/* Main Content Area */}
      <div className="flex-grow pl-16 relative overflow-y-auto h-screen scroll-smooth">
        {/* Header */}
        <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-200 px-8 lg:px-10 py-6 flex flex-col gap-5 xl:flex-row xl:justify-between xl:items-center transition-all">
          <div>
            <h2 className={`text-4xl font-black tracking-tight text-slate-900`}>
              {activeTab === 'overview' && 'Vue d\'ensemble'}
              {activeTab === 'scores' && 'Tableau de Bord'}
            </h2>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-violet-700">
                {sessionCode ? `Session ${sessionCode}` : 'Session -'}
              </span>
              {currentSession?.name && (
                <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600">
                  {currentSession.name}
                </span>
              )}
              <span className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-indigo-700">
                {isTeamlab ? 'TeamLab' : 'LeadLab'}
              </span>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${liveBadge.dotClass}`}></span>
              <span className={`text-xs font-bold uppercase tracking-wider ${liveBadge.textClass}`}>
                {liveBadge.label}
              </span>
            </div>
          </div>

          <div className="flex flex-col items-start gap-3 xl:items-end">
            {sessionCode && (
              <div className="flex flex-wrap items-center gap-2">
                <input
                  readOnly
                  value={playerLink}
                  className="min-w-[18rem] rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 shadow-sm"
                />
                <button
                  type="button"
                  onClick={copyPlayerLink}
                  className={`rounded-xl px-4 py-2 text-sm font-bold shadow-sm transition-colors ${
                    linkCopied
                      ? 'bg-emerald-600 text-white'
                      : 'border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100'
                  }`}
                >
                  {linkCopied ? 'Lien copié' : 'Copier lien de jeu'}
                </button>
              </div>
            )}
            {/* Horizontal Team Tabs */}
            <div className="flex flex-wrap items-center gap-1.5 bg-slate-100 p-1.5 rounded-2xl">
              {/* General Tab */}
              <button
                onClick={() => navigate(`/admin/decisions/0${sessionCode ? `?session=${sessionCode}` : ''}`)}
                className={`px-5 py-2.5 rounded-xl text-sm font-black transition-all duration-200 flex items-center gap-2 ${teamNumber === 0
                  ? 'bg-white text-slate-900 shadow-md ring-1 ring-black/5 scale-105'
                  : 'bg-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'
                  }`}
              >
                <span>Général</span>
              </button>
              <div className="w-px h-6 bg-slate-200 mx-2 hidden sm:block"></div>
              {[1, 2, 3, 4].map((num) => (
                <button
                  key={num}
                  onClick={() => navigate(`/admin/decisions/${num}${sessionCode ? `?session=${sessionCode}` : ''}`)}
                  className={`px-4 py-2.5 rounded-xl text-sm font-black transition-all duration-200 flex items-center gap-2 ${teamNumber === num
                    ? 'bg-white text-slate-900 shadow-md ring-1 ring-black/5 scale-105'
                    : 'bg-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'
                    }`}
                >
                  <span>Équipe {num}</span>
                </button>
              ))}
            </div>

          </div>
        </div>
        <div className="px-6 py-8 lg:px-10">
          <div className="space-y-10 min-h-[calc(100vh-300px)]">

          {/* --- VIEW: GLOBAL DASHBOARD (Team 0) --- */}
          {teamNumber === 0 && globalScores && (
            <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="flex items-center gap-4 px-2">
                <div className="w-12 h-12 rounded-2xl bg-slate-900 flex items-center justify-center shadow-lg shadow-slate-200">
                  <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-black text-slate-900 tracking-tight">Statistiques des Équipes</h3>
                  <p className="text-slate-500 font-medium">Indicateurs de performance par équipe</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                {[1, 2, 3, 4].map((num) => {
                  const teamScore = globalScores.find(s => s.teamNumber === num);
                  const tCfg = teamConfigs[num - 1];
                  const tColor = teamColorUtils[tCfg.color as keyof typeof teamColorUtils];
                  const progress = teamProgress[num]?.roundsPlayed ?? 0;
                  const progressPercent = getProgressPercent(progress);
                  const isCompleted = teamProgress[num]?.isCompleted ?? false;
                  // Calculate total score or average if needed, here just using impact1 as main metric or sum?
                  // Let's just display the 3/4 impacts.

                  return (
                    <div
                      key={num}
                      className={`rounded-xl shadow-sm border overflow-hidden hover:shadow-md transition-all duration-300 group ${
                        isCompleted
                          ? 'bg-emerald-50/60 border-emerald-200 shadow-emerald-100'
                          : 'bg-white border-slate-200'
                      }`}
                    >
                      {/* Header */}
                      <div className={`p-4 border-b flex items-center justify-between ${isCompleted ? 'bg-emerald-50 border-emerald-100' : 'bg-white border-slate-100'}`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-md flex items-center justify-center border border-slate-100 shadow-sm ${tColor.bg} ${tColor.text}`}>
                            {tCfg.icon}
                          </div>
                          <h4 className="text-lg font-bold text-slate-900">Équipe {num}</h4>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${isCompleted ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                            {progressPercent}%
                          </span>
                          {isCompleted && (
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-600 text-white shadow-sm" title="Session terminée">
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                              </svg>
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Scores Body */}
                      {/* Scores Body */}
                      <div className="p-5 space-y-5">
                        <div className={`rounded-xl border px-3 py-2.5 ${isCompleted ? 'border-emerald-200 bg-white/80' : 'border-slate-200 bg-slate-50/70'}`}>
                          <div className="mb-2 flex items-center justify-between">
                            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Progression</span>
                            <span className={`text-sm font-bold ${isCompleted ? 'text-emerald-700' : 'text-slate-700'}`}>
                              {progressPercent}%
                            </span>
                          </div>
                          <AnimatedProgressBar value={progressPercent} color="bg-emerald-500" />
                        </div>
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{impactLabels.impact1}</span>
                            <span className="text-lg font-bold text-slate-900">{teamScore?.finalScores.impact1 ?? '-'}</span>
                          </div>
                          <AnimatedProgressBar value={teamScore?.finalScores.impact1 ?? 0} color="bg-blue-500" />
                        </div>

                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{impactLabels.impact2}</span>
                            <span className="text-lg font-bold text-slate-900">{teamScore?.finalScores.impact2 ?? '-'}</span>
                          </div>
                          <AnimatedProgressBar value={teamScore?.finalScores.impact2 ?? 0} color="bg-pink-500" />
                        </div>

                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{impactLabels.impact3}</span>
                            <span className="text-lg font-bold text-slate-900">{teamScore?.finalScores.impact3 ?? '-'}</span>
                          </div>
                          <AnimatedProgressBar value={teamScore?.finalScores.impact3 ?? 0} color="bg-amber-500" />
                        </div>

                        {!isTeamlab && (
                          <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{impactLabels.impact4}</span>
                              <span className="text-lg font-bold text-slate-900">{teamScore?.finalScores.impact4 ?? '-'}</span>
                            </div>
                            <AnimatedProgressBar value={teamScore?.finalScores.impact4 ?? 0} color="bg-emerald-500" />
                          </div>
                        )}
                      </div>

                      {/* Footer Action */}
                      <div className="p-4 bg-slate-50 border-t border-slate-100 group-hover:bg-slate-100 transition-colors">
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            onClick={() => navigate(`/admin/decisions/${num}${sessionCode ? `?session=${sessionCode}` : ''}`)}
                            className="w-full py-3 rounded-xl bg-white border border-slate-200 text-slate-700 font-bold text-sm hover:bg-slate-800 hover:text-white hover:border-slate-800 transition-all shadow-sm"
                          >
                            Voir détails
                          </button>
                          <button
                            onClick={() => openResetModal(num)}
                            className="w-full py-3 rounded-xl bg-red-50 border border-red-200 text-red-600 font-bold text-sm hover:bg-red-100 transition-all shadow-sm"
                          >
                            Réinitialiser
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* --- GLOBAL RANKING --- */}
              <div className="pt-6">
                <div className="flex items-center gap-3 px-1 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-slate-900 text-white flex items-center justify-center shadow-sm">
                    <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">Classement Général</h3>
                    <p className="text-slate-500 text-sm">Classement base sur la somme des dimensions</p>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider pl-6 w-20">Rang</th>
                        <th className="p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Équipe</th>
                        <SortableHeader
                          label="Total"
                          active={rankingSort.column === 'total'}
                          direction={rankingSort.direction}
                          onClick={() => toggleRankingSort('total')}
                        />
                        <SortableHeader
                          label={impactLabels.impact1}
                          active={rankingSort.column === 'impact1'}
                          direction={rankingSort.direction}
                          onClick={() => toggleRankingSort('impact1')}
                        />
                        <SortableHeader
                          label={impactLabels.impact2}
                          active={rankingSort.column === 'impact2'}
                          direction={rankingSort.direction}
                          onClick={() => toggleRankingSort('impact2')}
                        />
                        <SortableHeader
                          label={impactLabels.impact3}
                          active={rankingSort.column === 'impact3'}
                          direction={rankingSort.direction}
                          onClick={() => toggleRankingSort('impact3')}
                        />
                        {!isTeamlab && (
                          <SortableHeader
                            label={impactLabels.impact4}
                            active={rankingSort.column === 'impact4'}
                            direction={rankingSort.direction}
                            onClick={() => toggleRankingSort('impact4')}
                          />
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {sortedGlobalScores.map((team, index) => {
                          const config = teamConfigs[Math.max(0, team.teamNumber - 1) % 4];
                          const tColor = teamColorUtils[config.color as keyof typeof teamColorUtils];
                          const totalScore = getTotalScore(team.finalScores);

                          return (
                            <tr key={team.teamNumber} className="hover:bg-slate-50 transition-colors">
                              <td className="px-6 py-4 font-mono text-sm font-semibold text-slate-400">
                                {index + 1}
                              </td>
                              <td className="px-4 py-4">
                                <div className="flex items-center gap-3">
                                  <div className={`w-8 h-8 rounded-md flex items-center justify-center border border-slate-100 shadow-sm ${tColor.bg} ${tColor.text}`}>
                                    {config.icon}
                                  </div>
                                  <span className="text-sm font-bold text-slate-900">Équipe {team.teamNumber}</span>
                                </div>
                              </td>
                              <td className="px-4 py-4 text-center">
                                <span className="text-sm font-black text-slate-900">{totalScore}</span>
                              </td>
                              <td className="px-4 py-4 text-center">
                                <span className="text-sm font-bold text-slate-900">{team.finalScores.impact1}</span>
                              </td>
                              <td className="px-4 py-4 text-center">
                                <span className="text-sm font-bold text-slate-900">{team.finalScores.impact2}</span>
                              </td>
                              <td className="px-4 py-4 text-center">
                                <span className="text-sm font-bold text-slate-900">{team.finalScores.impact3}</span>
                              </td>
                              {!isTeamlab && (
                                <td className="px-4 py-4 text-center">
                                  <span className="text-sm font-bold text-slate-900">{team.finalScores.impact4 ?? '-'}</span>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>

                <div className="mt-6 rounded-[1.5rem] border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-slate-50 p-5 shadow-sm">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div>
                      <h4 className="text-lg font-black text-slate-900">Évolution du classement</h4>
                      <p className="text-sm text-slate-500">Courbe du total cumulé par équipe pour voir qui passe devant au fil des périodes.</p>
                    </div>
                  </div>
                  <div ref={rankingChartContainerRef} className="w-full min-h-[320px]"></div>
                </div>
              </div>
            </div>
          )}

          {/* --- VIEW: INDIVIDUAL TEAMS --- */}
          {teamNumber > 0 && (
            <div className="space-y-16 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-20">

              <div className={`rounded-2xl border p-6 shadow-sm ${
                currentTeamCompleted
                  ? 'border-emerald-200 bg-emerald-50/70'
                  : `${theme.border} bg-white`
              }`}>
                <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`flex h-14 w-14 items-center justify-center rounded-2xl shadow-sm ${
                      currentTeamCompleted ? 'bg-emerald-100 text-emerald-600' : `${theme.bg} ${theme.text}`
                    }`}>
                      <div className="h-7 w-7">
                        {config.icon}
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="text-2xl font-black tracking-tight text-slate-900">Équipe {teamNumber}</h3>
                        {currentTeamCompleted && (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white">
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                            </svg>
                            Terminée
                          </span>
                        )}
                      </div>
                      <p className={`mt-1 font-medium ${currentTeamCompleted ? 'text-emerald-700' : 'text-slate-500'}`}>
                        {currentTeamCompleted
                          ? 'Cette équipe a terminé toutes les périodes de la session.'
                          : 'Cette équipe est encore en cours de progression.'}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <div className={`rounded-xl border px-4 py-3 ${
                      currentTeamCompleted ? 'border-emerald-200 bg-white/80' : 'border-slate-200 bg-slate-50'
                    }`}>
                      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Progression</div>
                      <div className="mt-2 min-w-[220px]">
                        <div className="mb-2 flex items-center justify-between">
                          <span className="text-xs font-semibold text-slate-500">
                            {Math.min(currentTeamRoundsPlayed, maxRounds)} / {maxRounds} périodes
                          </span>
                          <span className={`text-xl font-black ${currentTeamCompleted ? 'text-emerald-700' : 'text-slate-900'}`}>
                            {currentTeamProgressPercent}%
                          </span>
                        </div>
                        <AnimatedProgressBar value={currentTeamProgressPercent} color="bg-emerald-500" />
                      </div>
                    </div>
                    <button
                      onClick={() => openResetModal(teamNumber)}
                      className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-600 transition-colors hover:bg-red-100"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Réinitialiser les données de l'équipe
                    </button>
                  </div>
                </div>
              </div>

              {/* SECTION: GRAPH */}
              <div className="space-y-6">
                <div className="flex items-center justify-between gap-4 px-2">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-2xl ${theme.bg} flex items-center justify-center shadow-sm`}>
                      <svg className={`w-6 h-6 ${theme.text}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-2xl font-black text-slate-900 tracking-tight">{t('admin.results_chart_title')}</h3>
                      <p className="text-slate-500 font-medium">Évolution des indicateurs clés sur la session</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={toggleGraphFullscreen}
                    className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-sm transition-colors hover:border-slate-300 hover:text-slate-900"
                    title={isGraphFullscreen ? 'Quitter le plein écran' : 'Mettre le graph en plein écran'}
                    aria-label={isGraphFullscreen ? 'Quitter le plein écran' : 'Mettre le graph en plein écran'}
                  >
                    {isGraphFullscreen ? (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 15H5v4m0 0l5-5m4 1h4v4m0 0l-5-5M15 9h4V5m0 0l-5 5M9 9H5V5m0 0l5 5" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 3H5a2 2 0 00-2 2v3m16-5h-3m3 0v3M8 21H5a2 2 0 01-2-2v-3m16 5h-3m3 0v-3" />
                      </svg>
                    )}
                  </button>
                </div>

                <div
                  ref={graphCardRef}
                  className={`bg-white shadow-sm border border-slate-200 overflow-hidden relative ${
                    isGraphFullscreen ? 'flex h-screen items-center rounded-none p-6' : 'rounded-lg'
                  }`}
                >
                  <div className={`absolute top-0 right-0 w-96 h-96 ${theme.text} opacity-[0.03] transform pointer-events-none -rotate-12 translate-x-24 -translate-y-24`}>
                    {config.watermark}
                  </div>
                  <div
                    className={`relative z-10 w-full ${
                      isGraphFullscreen ? 'mx-auto max-w-7xl' : 'p-4 pt-6'
                    }`}
                  >
                    {graphData && graphData.datasets.length > 0 ? (
                      <div
                        ref={chartContainerRef}
                        className={`w-full ${isGraphFullscreen ? 'h-[calc(100vh-8rem)]' : 'h-[400px]'}`}
                      ></div>
                    ) : (
                      <div className={`${isGraphFullscreen ? 'h-[calc(100vh-8rem)]' : 'h-64'} flex flex-col items-center justify-center text-slate-400`}>
                        <svg className="w-10 h-10 mb-2 text-slate-200" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        <span className="font-medium text-sm">Aucune donnée disponible</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* SECTION: DECISIONS HISTORY MATRIX */}
              {matrixData && decisionsData && (
                <div className="space-y-6">
                  <div className="flex items-center gap-4 px-2">
                    <div className={`w-12 h-12 rounded-2xl ${theme.bg} flex items-center justify-center shadow-sm`}>
                      <svg className={`w-6 h-6 ${theme.text}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-2xl font-black text-slate-900 tracking-tight">Matrice des Choix</h3>
                      <p className="text-slate-500 font-medium">Répartition des points par critère et période</p>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200">
                            <th className="p-4 pl-6 text-xs font-bold text-slate-500 uppercase tracking-widest w-1/3">Critère</th>
                            {decisionsData.rounds.map((r) => (
                              <th key={r.roundNumber} className="p-4 text-center text-xs font-bold text-slate-500 uppercase tracking-widest min-w-[100px]">
                                Période {r.roundNumber}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {matrixData.map((row) => (
                            <tr key={row.id} className="group hover:bg-slate-50/50 transition-colors">
                              <td className="p-4 pl-6">
                                <div className="flex items-start gap-4">
                                  <div className={`mt-1 flex-shrink-0 w-3 h-3 rounded-full ${row.theme.badge.split(' ')[0]} ring-4 ring-white shadow-sm`}></div>
                                  <div>
                                    <div className={`font-bold text-sm mb-1 ${row.theme.text}`}>{row.name}</div>
                                    <div className="text-xs text-slate-500 leading-relaxed max-w-sm">{row.desc}</div>
                                  </div>
                                </div>
                              </td>
                              {row.rowScores.map((score, idx) => (
                                <td key={idx} className="p-4 text-center">
                                  {score > 0 ? (
                                    <span className={`inline-flex items-center justify-center min-w-[2.5rem] py-1 px-2 rounded-lg font-bold text-sm ${row.theme.badge} shadow-sm transform group-hover:scale-110 transition-transform`}>
                                      {score}
                                    </span>
                                  ) : (
                                    <span className="text-slate-300">-</span>
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* SECTION: SCORES TABLE (Replicated for Overview dashboard feel) */}
              <div className="space-y-6">
                <div className="flex items-center gap-4 px-2">
                  <div className={`w-12 h-12 rounded-2xl ${theme.bg} flex items-center justify-center shadow-sm`}>
                    <svg className={`w-6 h-6 ${theme.text}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-2xl font-black text-slate-900 tracking-tight">Détail des Scores</h3>
                    <p className="text-slate-500 font-medium">Historique complet points par période</p>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <th className="p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider pl-6">Période</th>
                          <th className="p-4 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">{impactLabels.impact1}</th>
                          <th className="p-4 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">{impactLabels.impact2}</th>
                          <th className="p-4 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">{impactLabels.impact3}</th>
                          {!isTeamlab && <th className="p-4 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">{impactLabels.impact4}</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        <tr className="bg-slate-50/30">
                          <td className="px-6 py-3 font-medium text-slate-400 text-sm pl-6">Départ</td>
                          <td className="px-6 py-3 text-center font-medium text-slate-400">{scoresData?.baseScore}</td>
                          <td className="px-6 py-3 text-center font-medium text-slate-400">{scoresData?.baseScore}</td>
                          <td className="px-6 py-3 text-center font-medium text-slate-400">{scoresData?.baseScore}</td>
                          {!isTeamlab && <td className="px-6 py-3 text-center font-medium text-slate-400">{scoresData?.baseScore}</td>}
                        </tr>
                        {scoresData?.rounds.map((round) => (
                          <tr key={round.roundNumber} className="hover:bg-slate-50 transition-colors">
                            <td className="px-6 py-3 font-semibold text-slate-700 pl-6">
                              Période {round.roundNumber}
                            </td>
                            <td className="px-6 py-3 text-center text-lg font-bold text-slate-700">{round.scores.impact1}</td>
                            <td className="px-6 py-3 text-center text-lg font-bold text-slate-700">{round.scores.impact2}</td>
                            <td className="px-6 py-3 text-center text-lg font-bold text-slate-700">{round.scores.impact3}</td>
                            {!isTeamlab && <td className="px-6 py-3 text-center text-lg font-bold text-slate-700">{round.scores.impact4}</td>}
                          </tr>
                        ))}
                      </tbody>
                      {scoresData?.finalScores && (
                        <tfoot className="bg-slate-50 border-t border-slate-200">
                          <tr>
                            <td className="px-6 py-4 pl-6">
                              <span className="font-bold text-slate-900">Total / Final</span>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <div className="text-2xl font-bold text-blue-600">{scoresData.finalScores.impact1}</div>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <div className="text-2xl font-bold text-pink-600">{scoresData.finalScores.impact2}</div>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <div className="text-2xl font-bold text-amber-600">{scoresData.finalScores.impact3}</div>
                            </td>
                            {!isTeamlab && (
                              <td className="px-6 py-4 text-center">
                                <div className="text-2xl font-bold text-emerald-600">{scoresData.finalScores.impact4}</div>
                              </td>
                            )}
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              </div>

            </div>
          )}



          {/* --- VIEW: SCORES --- */}


          </div>
        </div>
      </div >

      {/* Reset Modal */}
      {
        showResetModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setShowResetModal(false)}></div>
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full relative z-10 p-6 overflow-hidden border border-slate-200">
              <div className="text-center mb-6">
                <div className="w-12 h-12 bg-red-50 text-red-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                </div>
                <h3 className="text-xl font-bold text-slate-900">Attention !</h3>
                <p className="text-slate-600 mt-2 text-sm">Vous êtes sur le point de réinitialiser toutes les données de <span className="text-slate-900 font-bold">l'Équipe {resetTargetTeamNumber ?? teamNumber}</span>.</p>
              </div>
              <div className="flex gap-3">
                <button onClick={() => { setShowResetModal(false); setResetTargetTeamNumber(null); }} className="flex-1 py-2.5 rounded-lg font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors text-sm">Annuler</button>
                <button onClick={handleReset} disabled={resetting} className="flex-1 py-2.5 rounded-lg font-semibold bg-red-600 text-white hover:bg-red-700 shadow-md shadow-red-100 transition-colors text-sm">
                  {resetting ? '...' : 'Confirmer'}
                </button>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};
